/**
 * GovernanceP3Page Component
 *
 * Dashboard for P3 governance hardening features:
 * - Consequence Tier Gating
 * - Earned Authority Lifecycle + FI-to-Authority
 * - Intent Capsules (HMAC)
 * - Standards Evolution (Invariant Promotion + Load-Bearing Classification)
 *
 * Copyright (c) 2026 Equilateral AI. All rights reserved.
 * Follows: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Table, Spinner, Select, Button, Tabs, TextInput, Alert, Modal, Progress } from 'flowbite-react';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
  ClockIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  // Types
  TierGateDecision,
  TierGateConfig,
  AuthorityLifecycleEvent,
  FIAuthorityMapping,
  IntentCapsule,
  InvariantCandidate,
  PromotedInvariant,
  LoadBearingElement,
  // Tier Gate
  getTierGateHistory,
  getTierGateConfig,
  updateTierGateConfig,
  // Authority & FI
  getAuthorityLifecycle,
  getFIAuthorityMapping,
  triggerDemotion,
  // Capsules
  getCapsuleDriftCheck,
  signCapsule,
  // Standards
  getInvariantCandidates,
  promoteInvariant,
  getInvariantHistory,
  getLoadBearingElements,
  runLoadBearingAnalysis,
} from '../api/governance-p3';

// =============================================================================
// VERDICT / STATUS HELPERS
// =============================================================================

const verdictColor = (v: string): string => {
  switch (v) {
    case 'PASS': return 'success';
    case 'HOLD': return 'warning';
    case 'DENY': return 'failure';
    case 'ESCALATE': return 'purple';
    default: return 'gray';
  }
};

const fiStatusColor = (s: string): string => {
  switch (s) {
    case 'NORMAL': return 'success';
    case 'WARNING': return 'warning';
    case 'DOWNGRADED': return 'indigo';
    case 'CRITICAL': return 'failure';
    default: return 'gray';
  }
};

const enforcementColor = (level: string): string => {
  switch (level) {
    case 'critical': return 'failure';
    case 'important': return 'warning';
    case 'preferential': return 'info';
    default: return 'gray';
  }
};

const classificationColor = (c: string): string => {
  switch (c) {
    case 'load_bearing': return 'failure';
    case 'supplementary': return 'warning';
    case 'decorative': return 'gray';
    default: return 'gray';
  }
};

// =============================================================================
// TAB 1: TIER GATE
// =============================================================================

const TierGateTab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [decisions, setDecisions] = useState<TierGateDecision[]>([]);
  const [config, setConfig] = useState<TierGateConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editTier, setEditTier] = useState<TierGateConfig | null>(null);
  const [editForm, setEditForm] = useState({ Default_Verdict: '', FI_Threshold: 0, Require_Approval: false });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [histRes, cfgRes] = await Promise.all([
        getTierGateHistory(clientId, { limit: 50 }),
        getTierGateConfig(clientId),
      ]);
      if (histRes.success) setDecisions(histRes.data?.Records || []);
      if (cfgRes.success) setConfig(cfgRes.data?.Records || []);
    } catch (err) {
      console.error('Failed to fetch tier gate data:', err);
      toast.error('Failed to load tier gate data');
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEditOpen = (tier: TierGateConfig) => {
    setEditTier(tier);
    setEditForm({
      Default_Verdict: tier.Default_Verdict,
      FI_Threshold: tier.FI_Threshold,
      Require_Approval: tier.Require_Approval,
    });
  };

  const handleEditSave = async () => {
    if (!editTier) return;
    try {
      const res = await updateTierGateConfig(clientId, {
        Tier: editTier.Tier,
        Default_Verdict: editForm.Default_Verdict,
        FI_Threshold: editForm.FI_Threshold,
        Require_Approval: editForm.Require_Approval,
      });
      if (res.success) {
        toast.success(`Updated ${editTier.Tier} config`);
        setEditTier(null);
        fetchData();
      } else {
        toast.error(res.message || 'Update failed');
      }
    } catch (err) {
      toast.error('Failed to update tier config');
    }
  };

  // Stats
  const stats = {
    total: decisions.length,
    pass: decisions.filter(d => d.Verdict === 'PASS').length,
    hold: decisions.filter(d => d.Verdict === 'HOLD').length,
    deny: decisions.filter(d => d.Verdict === 'DENY').length,
    escalate: decisions.filter(d => d.Verdict === 'ESCALATE').length,
  };

  const tierLabels = ['READ', 'WRITE', 'IRREVERSIBLE', 'EXTERNAL_SIDE_EFFECT'];

  if (isLoading) {
    return <div className="flex justify-center py-12"><Spinner size="xl" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card><p className="text-sm text-gray-500">Total Decisions</p><p className="text-2xl font-bold">{stats.total}</p></Card>
        <Card><p className="text-sm text-gray-500">Pass</p><p className="text-2xl font-bold text-green-600">{stats.pass}</p></Card>
        <Card><p className="text-sm text-gray-500">Hold</p><p className="text-2xl font-bold text-yellow-600">{stats.hold}</p></Card>
        <Card><p className="text-sm text-gray-500">Deny</p><p className="text-2xl font-bold text-red-600">{stats.deny}</p></Card>
        <Card><p className="text-sm text-gray-500">Escalate</p><p className="text-2xl font-bold text-purple-600">{stats.escalate}</p></Card>
      </div>

      {/* Config Cards */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Tier Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tierLabels.map(tierName => {
            const cfg = config.find(c => c.Tier === tierName);
            return (
              <Card key={tierName} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => cfg && handleEditOpen(cfg)}>
                <h4 className="font-semibold text-gray-900">{tierName.replace(/_/g, ' ')}</h4>
                {cfg ? (
                  <div className="space-y-2 mt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Default Verdict</span>
                      <Badge color={verdictColor(cfg.Default_Verdict)}>{cfg.Default_Verdict}</Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">FI Threshold</span>
                      <span className="font-medium">{cfg.FI_Threshold}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Approval Required</span>
                      <Badge color={cfg.Require_Approval ? 'warning' : 'gray'}>{cfg.Require_Approval ? 'Yes' : 'No'}</Badge>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 mt-2">No config found</p>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Recent Decisions Table */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Decisions</h3>
        {decisions.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No gate decisions recorded yet</p>
        ) : (
          <div className="overflow-x-auto">
            <Table hoverable>
              <Table.Head>
                <Table.HeadCell>Agent</Table.HeadCell>
                <Table.HeadCell>Requested</Table.HeadCell>
                <Table.HeadCell>Effective</Table.HeadCell>
                <Table.HeadCell>Verdict</Table.HeadCell>
                <Table.HeadCell>Reason</Table.HeadCell>
                <Table.HeadCell>FI Score</Table.HeadCell>
                <Table.HeadCell>Drift</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {decisions.slice(0, 25).map(d => (
                  <Table.Row key={d.Gate_Decision_ID} className="bg-white">
                    <Table.Cell className="font-medium text-gray-900">{d.Agent_Name}</Table.Cell>
                    <Table.Cell>{d.Requested_Tier}</Table.Cell>
                    <Table.Cell>{d.Effective_Tier}</Table.Cell>
                    <Table.Cell><Badge color={verdictColor(d.Verdict)}>{d.Verdict}</Badge></Table.Cell>
                    <Table.Cell className="max-w-xs truncate" title={d.Reason}>{d.Reason}</Table.Cell>
                    <Table.Cell>{d.FI_Score_At_Evaluation.toFixed(2)}</Table.Cell>
                    <Table.Cell>
                      {d.Drift_Detected && (
                        <ExclamationTriangleIcon className="w-5 h-5 text-amber-500" title="Drift detected" />
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Card>

      {/* Edit Modal */}
      <Modal show={editTier !== null} onClose={() => setEditTier(null)}>
        <Modal.Header>Edit {editTier?.Tier?.replace(/_/g, ' ')} Configuration</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Verdict</label>
              <Select value={editForm.Default_Verdict} onChange={e => setEditForm(f => ({ ...f, Default_Verdict: e.target.value }))}>
                <option value="PASS">PASS</option>
                <option value="HOLD">HOLD</option>
                <option value="DENY">DENY</option>
                <option value="ESCALATE">ESCALATE</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">FI Threshold</label>
              <TextInput
                type="number"
                value={editForm.FI_Threshold}
                onChange={e => setEditForm(f => ({ ...f, FI_Threshold: parseFloat(e.target.value) || 0 }))}
                min={0}
                max={1}
                step={0.01}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editForm.Require_Approval}
                onChange={e => setEditForm(f => ({ ...f, Require_Approval: e.target.checked }))}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label className="text-sm text-gray-700">Require Approval</label>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleEditSave}>Save</Button>
          <Button color="gray" onClick={() => setEditTier(null)}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// =============================================================================
// TAB 2: AUTHORITY & FI
// =============================================================================

const AuthorityFITab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [mappings, setMappings] = useState<FIAuthorityMapping[]>([]);
  const [events, setEvents] = useState<AuthorityLifecycleEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDemotionModal, setShowDemotionModal] = useState(false);
  const [demotionForm, setDemotionForm] = useState({ Agent_Name: '', Reason: '' });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [mapRes, evtRes] = await Promise.all([
        getFIAuthorityMapping(clientId),
        getAuthorityLifecycle(clientId, { limit: 30 }),
      ]);
      if (mapRes.success) setMappings(mapRes.data?.Records || []);
      if (evtRes.success) setEvents(evtRes.data?.Records || []);
    } catch (err) {
      console.error('Failed to fetch authority/FI data:', err);
      toast.error('Failed to load authority data');
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDemotion = async () => {
    if (!demotionForm.Agent_Name || !demotionForm.Reason) {
      toast.error('Agent name and reason are required');
      return;
    }
    try {
      const res = await triggerDemotion(clientId, demotionForm);
      if (res.success) {
        toast.success(`Demotion triggered for ${demotionForm.Agent_Name}`);
        setShowDemotionModal(false);
        setDemotionForm({ Agent_Name: '', Reason: '' });
        fetchData();
      } else {
        toast.error(res.message || 'Demotion failed');
      }
    } catch (err) {
      toast.error('Failed to trigger demotion');
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Spinner size="xl" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">FI Authority Mapping</h3>
        <Button color="red" size="sm" onClick={() => setShowDemotionModal(true)}>
          Trigger Demotion
        </Button>
      </div>

      {/* FI Authority Grid */}
      {mappings.length === 0 ? (
        <Alert color="info">No FI authority mappings found for this client.</Alert>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {mappings.map(m => (
            <Card key={m.Agent_Name}>
              <div className="flex justify-between items-start">
                <h4 className="font-semibold text-gray-900">{m.Agent_Name}</h4>
                <Badge color={fiStatusColor(m.FI_Status)}>{m.FI_Status}</Badge>
              </div>
              <div className="mt-3 space-y-2">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">FI Score</span>
                    <span className="font-medium">{m.FI_Score.toFixed(2)}</span>
                  </div>
                  <Progress
                    progress={Math.round(m.FI_Score * 100)}
                    color={m.FI_Score >= 0.7 ? 'green' : m.FI_Score >= 0.4 ? 'yellow' : 'red'}
                    size="sm"
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Effective Ceiling</span>
                  <span className="font-medium">{m.Effective_Ceiling}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Authority Lifecycle Timeline */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Authority Lifecycle Events</h3>
        {events.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No lifecycle events recorded yet</p>
        ) : (
          <div className="space-y-4">
            {events.map(evt => (
              <div key={evt.Event_ID} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <ArrowRightIcon className="w-5 h-5 text-indigo-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{evt.Agent_Name}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Badge color="gray">{evt.Previous_Maturity}</Badge>
                    <ArrowRightIcon className="w-3 h-3" />
                    <Badge color="info">{evt.New_Maturity}</Badge>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <Badge color="purple">{evt.Trigger_Type}</Badge>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(evt.Triggered_At).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Demotion Modal */}
      <Modal show={showDemotionModal} onClose={() => setShowDemotionModal(false)}>
        <Modal.Header>Trigger Agent Demotion</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <Alert color="warning">
              This will immediately downgrade the agent's authority level. This action is audited.
            </Alert>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
              {mappings.length > 0 ? (
                <Select
                  value={demotionForm.Agent_Name}
                  onChange={e => setDemotionForm(f => ({ ...f, Agent_Name: e.target.value }))}
                >
                  <option value="">Select agent...</option>
                  {mappings.map(m => (
                    <option key={m.Agent_Name} value={m.Agent_Name}>{m.Agent_Name}</option>
                  ))}
                </Select>
              ) : (
                <TextInput
                  value={demotionForm.Agent_Name}
                  onChange={e => setDemotionForm(f => ({ ...f, Agent_Name: e.target.value }))}
                  placeholder="Enter agent name"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <TextInput
                value={demotionForm.Reason}
                onChange={e => setDemotionForm(f => ({ ...f, Reason: e.target.value }))}
                placeholder="Reason for demotion"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="red" onClick={handleDemotion}>Confirm Demotion</Button>
          <Button color="gray" onClick={() => setShowDemotionModal(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// =============================================================================
// TAB 3: INTENT CAPSULES
// =============================================================================

const IntentCapsulesTab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [capsules, setCapsules] = useState<IntentCapsule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signForm, setSignForm] = useState({ Mandate: '', Keywords: '', TTL_Minutes: 60 });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getCapsuleDriftCheck(clientId, { limit: 50 });
      if (res.success) setCapsules(res.data?.Records || []);
    } catch (err) {
      console.error('Failed to fetch capsule data:', err);
      toast.error('Failed to load capsule data');
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSign = async () => {
    if (!signForm.Mandate) {
      toast.error('Mandate is required');
      return;
    }
    try {
      const keywords = signForm.Keywords.split(',').map(k => k.trim()).filter(Boolean);
      const res = await signCapsule(clientId, {
        Mandate: signForm.Mandate,
        Keywords: keywords,
        TTL_Minutes: signForm.TTL_Minutes,
      });
      if (res.success) {
        toast.success('Capsule signed successfully');
        setShowSignModal(false);
        setSignForm({ Mandate: '', Keywords: '', TTL_Minutes: 60 });
        fetchData();
      } else {
        toast.error(res.message || 'Signing failed');
      }
    } catch (err) {
      toast.error('Failed to sign capsule');
    }
  };

  const isExpired = (expiresAt: string): boolean => {
    return new Date(expiresAt) < new Date();
  };

  const formatCountdown = (expiresAt: string): string => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return 'Expired';
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m`;
    return `${mins}m`;
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Spinner size="xl" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Active Capsules</h3>
        <Button color="blue" size="sm" onClick={() => setShowSignModal(true)}>
          Sign New Capsule
        </Button>
      </div>

      {/* Capsules Table */}
      <Card>
        {capsules.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No intent capsules found</p>
        ) : (
          <div className="overflow-x-auto">
            <Table hoverable>
              <Table.Head>
                <Table.HeadCell>Mandate</Table.HeadCell>
                <Table.HeadCell>Keywords</Table.HeadCell>
                <Table.HeadCell>Drift Score</Table.HeadCell>
                <Table.HeadCell>Expires</Table.HeadCell>
                <Table.HeadCell>Drift</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {capsules.map(c => (
                  <Table.Row key={c.Capsule_ID} className="bg-white">
                    <Table.Cell className="max-w-xs truncate font-medium text-gray-900" title={c.Mandate}>
                      {c.Mandate}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex flex-wrap gap-1">
                        {c.Keywords.slice(0, 4).map((kw, i) => (
                          <Badge key={i} color="info">{kw}</Badge>
                        ))}
                        {c.Keywords.length > 4 && (
                          <Badge color="gray">+{c.Keywords.length - 4}</Badge>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="w-24">
                        <Progress
                          progress={Math.round(c.Drift_Score * 100)}
                          color={c.Drift_Score >= 0.3 ? 'red' : c.Drift_Score >= 0.15 ? 'yellow' : 'green'}
                          size="sm"
                        />
                        <span className="text-xs text-gray-500">{c.Drift_Score.toFixed(2)}</span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {isExpired(c.Expires_At) ? (
                        <Badge color="failure">Expired</Badge>
                      ) : (
                        <div className="flex items-center gap-1 text-sm">
                          <ClockIcon className="w-4 h-4 text-gray-400" />
                          {formatCountdown(c.Expires_At)}
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {c.Drift_Detected && (
                        <ExclamationTriangleIcon className="w-5 h-5 text-amber-500" title="Drift detected" />
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Card>

      {/* Sign Modal */}
      <Modal show={showSignModal} onClose={() => setShowSignModal(false)}>
        <Modal.Header>Sign New Intent Capsule</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mandate</label>
              <TextInput
                value={signForm.Mandate}
                onChange={e => setSignForm(f => ({ ...f, Mandate: e.target.value }))}
                placeholder="Describe the intent mandate"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Keywords (comma-separated)</label>
              <TextInput
                value={signForm.Keywords}
                onChange={e => setSignForm(f => ({ ...f, Keywords: e.target.value }))}
                placeholder="deploy, staging, review"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">TTL (minutes)</label>
              <TextInput
                type="number"
                value={signForm.TTL_Minutes}
                onChange={e => setSignForm(f => ({ ...f, TTL_Minutes: parseInt(e.target.value) || 60 }))}
                min={1}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSign}>Sign Capsule</Button>
          <Button color="gray" onClick={() => setShowSignModal(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// =============================================================================
// TAB 4: STANDARDS EVOLUTION
// =============================================================================

const StandardsEvolutionTab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [candidates, setCandidates] = useState<InvariantCandidate[]>([]);
  const [promoted, setPromoted] = useState<PromotedInvariant[]>([]);
  const [elements, setElements] = useState<LoadBearingElement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [promoteForm, setPromoteForm] = useState<{ Candidate_ID: string; Enforcement_Level: string; Tier: string } | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [candRes, histRes, elemRes] = await Promise.all([
        getInvariantCandidates(clientId),
        getInvariantHistory(clientId),
        getLoadBearingElements(clientId),
      ]);
      if (candRes.success) setCandidates(candRes.data?.Records || []);
      if (histRes.success) setPromoted(histRes.data?.Records || []);
      if (elemRes.success) setElements(elemRes.data?.Records || []);
    } catch (err) {
      console.error('Failed to fetch standards data:', err);
      toast.error('Failed to load standards evolution data');
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePromote = async () => {
    if (!promoteForm) return;
    try {
      const res = await promoteInvariant(clientId, promoteForm);
      if (res.success) {
        toast.success('Invariant promoted');
        setPromoteForm(null);
        fetchData();
      } else {
        toast.error(res.message || 'Promotion failed');
      }
    } catch (err) {
      toast.error('Failed to promote invariant');
    }
  };

  const handleRunAnalysis = async () => {
    setIsAnalyzing(true);
    try {
      const res = await runLoadBearingAnalysis(clientId, { Force_Refresh: true });
      if (res.success) {
        toast.success('Load-bearing analysis started');
        // Refresh after a short delay to pick up results
        setTimeout(fetchData, 2000);
      } else {
        toast.error(res.message || 'Analysis failed');
      }
    } catch (err) {
      toast.error('Failed to run analysis');
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Spinner size="xl" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Invariant Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Candidates */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Invariant Candidates</h3>
          {candidates.length === 0 ? (
            <p className="text-gray-500 text-center py-6">No candidates pending promotion</p>
          ) : (
            <div className="space-y-3">
              {candidates.map(c => (
                <div key={c.Candidate_ID} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-900 mb-2">{c.Rule_Text}</p>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge color="info">{c.Domain}</Badge>
                    <span className="text-gray-500">Occurrences: {c.Occurrence_Count}</span>
                    <span className="text-gray-500">Score: {c.Promotion_Score.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-gray-500">Tests:</span>
                    <span title="Universality">
                      {c.Tests.universality
                        ? <CheckCircleIcon className="w-4 h-4 text-green-500 inline" />
                        : <XCircleIcon className="w-4 h-4 text-red-400 inline" />
                      }
                    </span>
                    <span title="Consequence">
                      {c.Tests.consequence
                        ? <CheckCircleIcon className="w-4 h-4 text-green-500 inline" />
                        : <XCircleIcon className="w-4 h-4 text-red-400 inline" />
                      }
                    </span>
                    <span title="Context">
                      {c.Tests.context
                        ? <CheckCircleIcon className="w-4 h-4 text-green-500 inline" />
                        : <XCircleIcon className="w-4 h-4 text-red-400 inline" />
                      }
                    </span>
                    <Button
                      size="xs"
                      color="blue"
                      className="ml-auto"
                      onClick={() => setPromoteForm({ Candidate_ID: c.Candidate_ID, Enforcement_Level: 'important', Tier: 'READ' })}
                    >
                      Promote
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Promoted Invariants */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Promoted Invariants</h3>
          {promoted.length === 0 ? (
            <p className="text-gray-500 text-center py-6">No invariants promoted yet</p>
          ) : (
            <div className="space-y-3">
              {promoted.map(p => (
                <div key={p.Invariant_ID} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-900 mb-2">{p.Rule_Text}</p>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge color={enforcementColor(p.Enforcement_Level)}>{p.Enforcement_Level}</Badge>
                    <Badge color="gray">{p.Domain}</Badge>
                    <Badge color="gray">Tier: {p.Tier}</Badge>
                    <span className="text-gray-500">Violations: {p.Violation_Count}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Load-Bearing Section */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Load-Bearing Classification</h3>
          <Button size="sm" color="blue" onClick={handleRunAnalysis} disabled={isAnalyzing}>
            {isAnalyzing ? <Spinner size="sm" className="mr-2" /> : <ArrowPathIcon className="w-4 h-4 mr-2" />}
            Run Analysis
          </Button>
        </div>
        {elements.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No elements classified yet. Run an analysis to get started.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {elements.map(el => (
              <div key={el.Element_ID} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{el.Element_Name}</p>
                    <p className="text-xs text-gray-500">{el.Element_Type}</p>
                  </div>
                  <Badge color={classificationColor(el.Classification)}>
                    {el.Classification.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div className="mt-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-500">Failure Impact</span>
                    <span className="font-medium">{el.Failure_Impact_Score.toFixed(2)}</span>
                  </div>
                  <Progress
                    progress={Math.round(el.Failure_Impact_Score * 100)}
                    color={el.Failure_Impact_Score >= 0.7 ? 'red' : el.Failure_Impact_Score >= 0.4 ? 'yellow' : 'green'}
                    size="sm"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Promote Modal */}
      <Modal show={promoteForm !== null} onClose={() => setPromoteForm(null)}>
        <Modal.Header>Promote Invariant</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Enforcement Level</label>
              <Select
                value={promoteForm?.Enforcement_Level || 'important'}
                onChange={e => setPromoteForm(f => f ? { ...f, Enforcement_Level: e.target.value } : null)}
              >
                <option value="critical">Critical</option>
                <option value="important">Important</option>
                <option value="preferential">Preferential</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
              <Select
                value={promoteForm?.Tier || 'READ'}
                onChange={e => setPromoteForm(f => f ? { ...f, Tier: e.target.value } : null)}
              >
                <option value="READ">READ</option>
                <option value="WRITE">WRITE</option>
                <option value="IRREVERSIBLE">IRREVERSIBLE</option>
                <option value="EXTERNAL_SIDE_EFFECT">EXTERNAL SIDE EFFECT</option>
              </Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handlePromote}>Promote</Button>
          <Button color="gray" onClick={() => setPromoteForm(null)}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// =============================================================================
// MAIN PAGE
// =============================================================================

export const GovernanceP3Page: React.FC = () => {
  const { user } = useAuth();
  const clientId = user?.client_id;

  if (!clientId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Governance P3</h1>
        <Alert color="warning">No client context available. Please select a client to continue.</Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <ShieldCheckIcon className="w-7 h-7 text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900">Governance P3</h1>
        </div>
        <p className="text-gray-600 mt-1">
          Consequence tier gating, earned authority lifecycle, intent capsules, and standards evolution
        </p>
      </div>

      {/* Tabbed Dashboard */}
      <Tabs aria-label="Governance P3 tabs" variant="underline">
        <Tabs.Item active title="Tier Gate">
          <TierGateTab clientId={clientId} />
        </Tabs.Item>
        <Tabs.Item title="Authority & FI">
          <AuthorityFITab clientId={clientId} />
        </Tabs.Item>
        <Tabs.Item title="Intent Capsules">
          <IntentCapsulesTab clientId={clientId} />
        </Tabs.Item>
        <Tabs.Item title="Standards Evolution">
          <StandardsEvolutionTab clientId={clientId} />
        </Tabs.Item>
      </Tabs>
    </div>
  );
};

export default GovernanceP3Page;
